import { useEffect, useMemo, useRef, useState } from 'react';
import { useAtom as useStateSegment } from 'jotai';
import { Controller, useForm } from 'react-hook-form';
import { translate } from '../../../localization';
import Button from '../../atoms/Button';
import FilterDropdown from '../../molecules/FilterDropdown';
import styles from './BrandDetailsFilter.module.css';
import useBrandDetailsInputOptions from '../../../hooks/dsp/useBrandDetailsInputOptions';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import { FormFields } from '../../../types/PromptForm';
import {
  GroupByConfig,
  GroupTooltipConfig,
  Option,
} from '../../../types/SelectInput';
import { BrandCategory, CannedParams } from '../../../types/Content';
import {
  ApiPayloadUsed,
  knowledgeCenterStateSegment,
} from '../../../store/markettingContentGeneration/stateSegments/brandsInformationStateSegment';

export enum BrandsInfoPageFormVariants {
  FILTERS_FORM = 'filters-form',
}

type BrandDetailsFormField = {
  country: string;
  brand: string;
  indication?: string;
  targetAudience?: (string | undefined)[];
  topics?: (string | undefined)[];
};

const defaultValues: BrandDetailsFormField = {
  country: '',
  brand: '',
  indication: '',
  targetAudience: [],
  topics: [],
};

type BrandDetailsFilterProps = {
  onFiltersApplied: (
    newApiPayload: ApiPayloadUsed,
    isNonOnboarded: boolean
  ) => void;
};

const BrandDetailsFilter: React.FC<BrandDetailsFilterProps> = ({
  onFiltersApplied,
}) => {
  const initialRef = useRef(false);
  const formRef = useRef<HTMLFormElement>(null);
  const [brandsInformation, setBrandInformation] = useStateSegment(
    knowledgeCenterStateSegment
  );

  // Get feature flags
  const { isAemAssetIntegrationEnabled } = useFeatureFlags();

  const [currentBrand, setCurrentBrand] = useState('');
  const [currentCountry, setCurrentCountry] = useState('');
  const [currentCustomerTypes, setCurrentCustomerTypes] = useState<string[]>(
    []
  );
  const [currentPrimaryMessages, setCurrentPrimaryMessages] = useState<
    string[]
  >([]);

  const { control, getValues, handleSubmit, reset, setValue, watch } =
    useForm<BrandDetailsFormField>({
      // resolver: yupResolver(filtersSchema),
      mode: 'onChange',
      defaultValues,
    });

  const { countries: countriesList } = useBrandDetailsInputOptions({
    name: 'countries',
    formField: FormFields.COUNTRY,
    params: false,
  });

  const { brands: brandsList } = useBrandDetailsInputOptions({
    name: 'brands',
    formField: FormFields.BRAND,
    params: {
      countryName: getValues('country') || '',
      action: '',
    },
  });

  const { indications: indicationsList } = useBrandDetailsInputOptions({
    name: 'indications',
    formField: FormFields.INDICATION,
    params: {
      brandName: getValues('brand') || '',
      countryName: getValues('country') || '',
    },
  });

  const { audiences: customersList } = useBrandDetailsInputOptions({
    name: 'audiences',
    formField: FormFields.AUDIENCE,
    params: {
      brandName: getValues('brand') || '',
    },
  });

  const { primaryMessages: primaryMessagesList } = useBrandDetailsInputOptions({
    name: 'primaryMessages',
    formField: FormFields.PRIMARY_MESSAGES,
    params: {
      brandName: getValues('brand') || '',
      countryName: getValues('country') || '',
      indicationName: getValues('indication') || '',
      audiences: currentCustomerTypes || [],
    },
  });

  const { language: languagesList } = useBrandDetailsInputOptions({
    name: 'language',
    formField: FormFields.LANGUAGE,
    params: {
      countryName: getValues('country') || '',
    },
  });

  const handleSelection = (
    fieldName: 'brand' | 'indication' | 'country' | 'targetAudience' | 'topics',
    value: string | string[]
  ) => {
    setValue(fieldName, value);

    if (fieldName === 'country') {
      setCurrentCountry(value as string);
      setCurrentBrand('');
      setCurrentCustomerTypes([]);
      setCurrentPrimaryMessages([]);

      reset({
        ...watch(),
        brand: '',
        indication: '',
        targetAudience: [],
        topics: [],
      });
    }

    if (fieldName === 'brand') {
      setCurrentBrand(value as string);
      setCurrentCustomerTypes([]);
      setCurrentPrimaryMessages([]);

      reset({
        ...watch(),
        indication: '',
        targetAudience: [],
        topics: [],
      });
    }

    if (fieldName === 'topics') {
      setCurrentPrimaryMessages((value as string[]) || []);
    }

    if (fieldName === 'targetAudience') {
      setCurrentCustomerTypes((value as string[]) || []);
    }
  };

  useEffect(() => {
    if (currentBrand) {
      customersList.fetchOptions();
      indicationsList.fetchOptions();
      primaryMessagesList.fetchOptions();
    }
  }, [currentBrand]);

  useEffect(() => {
    if (currentCountry) {
      languagesList.fetchOptions();
      brandsList.fetchOptions();
    }
  }, [currentCountry]);

  useEffect(() => {
    if (
      !currentCustomerTypes.length &&
      customersList?.options &&
      customersList?.options?.length > 0
    ) {
      setCurrentCustomerTypes(
        customersList?.options?.map(opt => opt?.name as string)
      );
    }
  }, [customersList?.options]);

  useEffect(() => {
    if (
      brandsInformation.apiPayloadUsed.topics?.length === 0 &&
      !currentPrimaryMessages.length &&
      primaryMessagesList?.options &&
      primaryMessagesList?.options?.length > 0
    ) {
      setCurrentPrimaryMessages(
        primaryMessagesList?.options?.map(opt => opt?.name as string)
      );
    }
  }, [primaryMessagesList?.options, brandsInformation.apiPayloadUsed]);

  const isApplyBtnDisabled = !(getValues('country') && getValues('brand'));

  const handleApplyButtonClick = (values: BrandDetailsFormField) => {
    const { brand, country, indication, targetAudience, topics } = values;

    // Determine if the selected brand is non-onboarded
    const selectedBrand = brandsList?.options?.find(
      option => option.name === brand
    ) as CannedParams | undefined;
    const isNonOnboarded =
      selectedBrand?.category === BrandCategory.NON_ONBOARDED;

    const apiPayload = {
      brand: brand || getValues('brand') || '',
      country: country || getValues('country') || '',
      indication: indication || getValues('indication') || '',
      targetAudience: targetAudience || getValues('targetAudience') || [],
      languages: [],
      aboveMarketTeams: [],
      topics: topics || getValues('topics') || [],
      aemAssetIntegrationEnabled: isAemAssetIntegrationEnabled,
    };

    setBrandInformation(prev => ({
      ...prev,
      apiPayloadUsed: apiPayload,
      brandDetails: {
        data: {
          id: '',
          name: '',
          description: '',
          additionalBrandDetails: [],
          supportingInfo: [],
          indications: [],
        },
        isLoading: false,
        hasNoData: false,
        errorMessage: '',
      },
      segmentDetails: {
        data: {
          segments: [],
        },
        isLoading: false,
        hasNoData: false,
        errorMessage: '',
      },
      claimDetails: {
        data: {
          claimsPagination: {
            total: 0,
            page: 0,
            totalPages: 0,
            nextPage: null,
            previousPage: null,
          },
          claims: [],
        },
        isLoading: false,
        hasNoData: false,
        errorMessage: '',
      },
      images: {
        data: {
          imagesPagination: {
            total: 0,
            page: 0,
            totalPages: 0,
            nextPage: null,
            previousPage: null,
            nextPageToken: '',
          },
          images: [],
        },
        isLoading: false,
        hasNoData: false,
        errorMessage: '',
      },
    }));

    // Call the parent component's filter handler
    onFiltersApplied(apiPayload, isNonOnboarded);
  };
  useEffect(() => {
    if (!currentBrand && !currentCountry && !initialRef.current) {
      initialRef.current = true;
      const { brand, country, targetAudience, topics, indication } =
        brandsInformation.apiPayloadUsed;
      if (brand && country) {
        setCurrentBrand(brand);
        setCurrentCountry(country);
        if (targetAudience?.length) {
          setCurrentCustomerTypes(targetAudience as unknown as string[]);
        }
        if (topics?.length) {
          setCurrentPrimaryMessages(topics as unknown as string[]);
        }
        reset({
          brand,
          country,
          targetAudience,
          topics,
          indication,
        });
        handleSubmit(handleApplyButtonClick)();
      }
    }
  }, [brandsInformation.apiPayloadUsed]);

  const getFilterDropdownValue = (value: string | string[]) =>
    typeof value === 'string' ? [value] : value;

  const brandGroupingConfig: GroupByConfig[] = useMemo(() => {
    const brands = brandsList.options;
    const onboardedBrands: Option[] = [];
    const nonOnboardedBrands: Option[] = [];

    (brands as Option[])?.forEach((brand: Option) => {
      if (brand.category === BrandCategory.ONBOARDED) {
        onboardedBrands.push(brand);
      } else if (brand.category === BrandCategory.NON_ONBOARDED) {
        nonOnboardedBrands.push(brand);
      }
    });

    // Sort brands alphabetically within each group
    onboardedBrands.sort(({ label: labelA }, { label: labelB }) =>
      labelA.localeCompare(labelB)
    );
    nonOnboardedBrands.sort(({ label: labelA }, { label: labelB }) =>
      labelA.localeCompare(labelB)
    );

    const brandGroupConfig: GroupByConfig[] = [];

    // Add Amplify group (ONBOARDED brands) first
    if (onboardedBrands.length > 0) {
      brandGroupConfig.push({
        title: translate('brand_category_amplify'),
        groupBy: onboardedBrands.map(el => el.name) as string[],
      });
    }

    // Add Explore group (NON_ONBOARDED brands) second
    if (nonOnboardedBrands.length > 0) {
      brandGroupConfig.push({
        title: translate('brand_category_explore'),
        groupBy: nonOnboardedBrands.map(el => el.name) as string[],
      });
    }

    return brandGroupConfig;
  }, [brandsList.options]);

  const brandTooltipConfig: GroupTooltipConfig = useMemo(
    () => ({
      [translate('brand_category_amplify')]: {
        text: translate('brand_category_amplify_tooltip'),
      },
      [translate('brand_category_explore')]: {
        text: translate('brand_category_explore_tooltip'),
      },
    }),
    []
  );

  return (
    <form
      ref={formRef}
      onSubmit={event => {
        event.preventDefault();
        handleSubmit(
          values => {
            handleApplyButtonClick(values);
          },
          errors => {
            // eslint-disable-next-line no-console
            console.error('Validation failed!', errors);
          }
        )(event);
      }}
      className={`${styles.marketingform} pfz-cggenai-scrollable`}
      id={BrandsInfoPageFormVariants.FILTERS_FORM}
    >
      <div className={styles['filters--wrapper']}>
        <div className={styles['filter_inputs--wrapper']}>
          <Controller
            name='country'
            control={control}
            render={({ field }) => (
              <FilterDropdown
                name={field.name}
                list={countriesList?.options || []}
                searchInputPlaceholder={translate('search_for_a_country')}
                inputPlaceholder={translate('marketingbox_country_label')}
                handleSelection={(value: string | string[]) => {
                  field.onChange(value);
                  handleSelection('country', value);
                }}
                value={getFilterDropdownValue(field.value)}
                required
                isDisabled={
                  !countriesList?.options ||
                  countriesList?.options?.length === 0
                }
              />
            )}
          />

          <Controller
            name='brand'
            control={control}
            render={({ field }) => (
              <FilterDropdown
                name={field.name}
                list={brandsList?.options || []}
                searchInputPlaceholder={translate('search_for_a_brand')}
                inputPlaceholder={translate('marketingbox_brand_label')}
                handleSelection={(value: string | string[]) => {
                  field.onChange(value);
                  handleSelection('brand', value);
                }}
                value={getFilterDropdownValue(field.value)}
                required
                isDisabled={
                  !brandsList?.options ||
                  brandsList?.options?.length === 0 ||
                  !getValues('country')
                }
                groupingConfig={brandGroupingConfig}
                groupTooltips={brandTooltipConfig}
                isGroupDropDown
              />
            )}
          />

          <Controller
            name='indication'
            control={control}
            render={({ field }) => (
              <FilterDropdown
                name={field.name}
                list={indicationsList?.options || []}
                searchInputPlaceholder={translate('search_for_an_indication')}
                inputPlaceholder={translate('marketingbox_indications_label')}
                handleSelection={(value: string | string[]) => {
                  field.onChange(value);
                  handleSelection('indication', value);
                }}
                value={getFilterDropdownValue(field.value as string)}
                isDisabled={
                  !indicationsList?.options ||
                  indicationsList?.options?.length === 0 ||
                  !getValues('brand') ||
                  !getValues('country')
                }
                className={styles['filter-dropdown__height']}
              />
            )}
          />

          <Controller
            name='targetAudience'
            control={control}
            render={({ field }) => (
              <FilterDropdown
                name={field.name}
                list={customersList?.options || []}
                searchInputPlaceholder={translate('search_for_an_audience')}
                inputPlaceholder={translate('marketingbox_customer_type_label')}
                handleSelection={(value: string | string[]) => {
                  field.onChange(value);
                  handleSelection('targetAudience', value);
                }}
                value={currentCustomerTypes}
                multiSelect
                isDisabled={
                  !customersList?.options ||
                  customersList?.options?.length === 0 ||
                  !getValues('brand')
                }
                enableSelectAll
              />
            )}
          />

          <Controller
            name='topics'
            control={control}
            render={({ field }) => (
              <FilterDropdown
                name={field.name}
                list={primaryMessagesList?.options || []}
                searchInputPlaceholder={translate('search_for_a_topic')}
                inputPlaceholder={translate('marketingbox_topics')}
                handleSelection={(value: string | string[]) => {
                  field.onChange(value);
                  handleSelection('topics', value);
                }}
                value={currentPrimaryMessages}
                multiSelect
                isDisabled={
                  !primaryMessagesList?.options ||
                  primaryMessagesList?.options?.length === 0 ||
                  !getValues('brand') ||
                  !getValues('country')
                }
                className={styles['filter-dropdown__height']}
                enableSelectAll={currentPrimaryMessages.length === 0}
              />
            )}
          />
        </div>

        <Button
          type='submit'
          variant='solid'
          size='m'
          form={BrandsInfoPageFormVariants.FILTERS_FORM}
          colorScheme='primary'
          isDisabled={isApplyBtnDisabled}
          className={`${styles.button_submit} ${
            isApplyBtnDisabled ? styles['button_submit--disabled'] : ''
          }`}
        >
          {translate('common_apply')}
        </Button>
      </div>
    </form>
  );
};

export default BrandDetailsFilter;
